'use client';

/**
 * Shared plumbing for the Meridian chat API client.
 *
 * Split out of `../meridian-api-client.ts` (module-size rule). The envelope is
 * the contract that matters: EVERY client function catches and returns
 * `{ success: false, error }` rather than rejecting, because the react-query
 * hooks above read the envelope. A function here that throws would surface as an
 * unhandled rejection in the browser instead of an error state in the UI.
 */

/** API response wrapper type */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  cursor?: string | null;
  hasMore?: boolean;
}

/**
 * Log API calls for debugging.
 *
 * `console` rather than `@west-tennessee-consulting/logger` on purpose: this module is `'use client'`,
 * so it runs in the browser where the logger's server sinks (and the #Hub Errors
 * Slack forward) do not exist. STRUCTURED_LOGGING scopes its ban on `console.*`
 * to backend/server/cron code.
 */
export function logApiCall(method: string, endpoint: string, params?: unknown): void {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Meridian API] ${method} ${endpoint}`, params);
  }
}

/** Log API errors */
export function logApiError(method: string, endpoint: string, error: unknown): void {
  console.error(`[Meridian API] ${method} ${endpoint} failed:`, error);
}

/** Build query string from params, dropping every unset value. */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

/** Narrow an unknown thrown value to a message, with a per-call fallback. */
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}
